import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { ISerial } from "shared/interfaces/cadastrosN2";
import { IProjetista } from "shared/interfaces/projetista";

interface ModalCadastroSerialProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (serial: ISerial) => void;
  serialData?: ISerial;
  projetistas: IProjetista[];
  projetistasSelecionadosNoBloco?: number[]; // IDs dos projetistas selecionados no bloco principal
}

export const ModalCadastroSerial = ({
  isOpen,
  onClose,
  onSave,
  serialData,
  projetistas,
  projetistasSelecionadosNoBloco = [],
}: ModalCadastroSerialProps) => {
  const [serial, setSerial] = useState("");
  const [projetistasSelecionados, setProjetistasSelecionados] = useState<number[]>([]);
  const [filtro, setFiltro] = useState("");
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (serialData) {
      setSerial(serialData.serial);
      // Filtrar apenas os projetistas que estão selecionados no bloco principal
      setProjetistasSelecionados(
        serialData.projetistasIds.filter((id) => projetistasSelecionadosNoBloco.includes(id))
      );
    } else {
      setSerial("");
      setProjetistasSelecionados([]);
    }
    setFiltro("");
    setErro("");
  }, [serialData, isOpen, projetistasSelecionadosNoBloco]);

  // Filtrar projetistas: apenas os que estão selecionados no bloco principal
  const projetistasDisponiveis = projetistas.filter((p) =>
    projetistasSelecionadosNoBloco.includes(p.idProjetista)
  );

  // Aplicar filtro de busca nos projetistas disponíveis
  const projetistasFiltrados = projetistasDisponiveis.filter((p) =>
    p.nome.toLowerCase().includes(filtro.toLowerCase())
  );

  const handleSelecionarTodos = () => {
    if (projetistasSelecionados.length === projetistasDisponiveis.length) {
      setProjetistasSelecionados([]);
    } else {
      setProjetistasSelecionados(projetistasDisponiveis.map((p) => p.idProjetista));
    }
  };

  const handleToggleProjetista = (id: number) => {
    setProjetistasSelecionados((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleSalvar = () => {
    if (!serial.trim()) {
      setErro("Serial é obrigatório");
      return;
    }

    if (projetistasSelecionadosNoBloco.length === 0) {
      setErro("Selecione pelo menos um projetista no bloco 'Seleção de projetistas (Envio e visualização no MOF)' antes de adicionar seriais");
      return;
    }

    if (projetistasSelecionados.length === 0) {
      setErro("Selecione ao menos um projetista autorizado para este serial");
      return;
    }

    onSave({
      id: serialData?.id,
      serial: serial.trim(),
      projetistasIds: projetistasSelecionados,
    });
    handleClose();
  };

  const handleClose = () => {
    setSerial("");
    setProjetistasSelecionados([]);
    setFiltro("");
    setErro("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{serialData ? "Editar Serial" : "Adicionar Serial CEM PRO"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Serial"
          value={serial}
          onChange={(e) => {
            setSerial(e.target.value);
            setErro("");
          }}
          error={!!erro && !serial.trim()}
          helperText={!!erro && !serial.trim() ? "Campo obrigatório" : ""}
          sx={{ mt: 2, mb: 3 }}
          placeholder="Ex: CEM30-00002-000A"
        />

        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Seleção de projetistas autorizados (Envio ao MOF)
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Busque o projetista"
          variant="outlined"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {projetistasDisponiveis.length > 0 ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={projetistasSelecionados.length === projetistasDisponiveis.length}
                  indeterminate={
                    projetistasSelecionados.length > 0 &&
                    projetistasSelecionados.length < projetistasDisponiveis.length
                  }
                  onChange={handleSelecionarTodos}
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>SELECIONAR TODOS</Typography>}
            />

            <Box
              sx={{
                maxHeight: 300,
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: 1,
                p: 1,
              }}
            >
              {projetistasFiltrados.length > 0 ? (
                projetistasFiltrados.map((p) => (
                  <FormControlLabel
                    key={p.idProjetista}
                    control={
                      <Checkbox
                        checked={projetistasSelecionados.includes(p.idProjetista)}
                        onChange={() => handleToggleProjetista(p.idProjetista)}
                      />
                    }
                    label={p.nome}
                    sx={{ display: "block", mb: 0.5 }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                  Nenhum projetista encontrado
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              border: "1px solid #eee",
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
              Selecione pelo menos um projetista no bloco "Seleção de projetistas (Envio e visualização no MOF)" para poder adicionar seriais.
            </Typography>
          </Box>
        )}


        {erro && (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>
            {erro}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          CANCELAR
        </Button>
        <Button variant="contained" onClick={handleSalvar}>
          SALVAR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

